import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useRef, useState } from "react"

import { toast } from "@atlant/ui"

import { graphql } from "#gql"
import { queryKeys } from "#lib/query-keys.ts"
import { useLoggedInUserInfo } from "#store/user-info.ts"
import { client, GraphQLClientError } from "#utils/graphql-client.ts"

const InvoiceUploadAddDocument = graphql(/* GraphQL */ `
  mutation InvoiceUploadAdd($input: MutationInvoiceUploadAddInput!) {
    invoiceUploadAdd(input: $input) {
      id
    }
  }
`)

export const INVOICE_UPLOAD_ACCEPT =
  ".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"

// Mirrors the server's type check so a bad file fails before its bytes are sent
const ACCEPTED_NAME = /\.(pdf|png|jpe?g)$/i
// Batches run to dozens of files: a few at a time is quicker than one by one
const CONCURRENCY = 3
// The server's code for "this file is already in the queue"
const DUPLICATE_CODE = "INVOICE_UPLOAD_DUPLICATE"

type Outcome = { name: string } & (
  { result: "uploaded" | "skipped" } | { result: "failed"; reason: string }
)

const uploadFile = async (organizationId: string, file: File): Promise<Outcome> => {
  const name = file.name.normalize("NFC")
  if (!ACCEPTED_NAME.test(name)) {
    return { name, result: "failed", reason: "not a PDF, PNG or JPG" }
  }

  try {
    await client.request(InvoiceUploadAddDocument, {
      input: { organizationId, name, file },
    })
    return { name, result: "uploaded" }
  } catch (error) {
    if (error instanceof GraphQLClientError) {
      return error.response.errors[0]?.extensions?.code === DUPLICATE_CODE
        ? { name, result: "skipped" }
        : { name, result: "failed", reason: error.message }
    }
    return { name, result: "failed", reason: "upload failed" }
  }
}

// One toast per batch: "41 uploaded · 2 already in the queue · 1 failed"
const showSummary = (outcomes: Outcome[]) => {
  const uploaded = outcomes.filter((o) => o.result === "uploaded").length
  const skipped = outcomes.filter((o) => o.result === "skipped").length
  const failed = outcomes.flatMap((o) => (o.result === "failed" ? [o] : []))

  const title = [
    uploaded > 0 && `${uploaded} uploaded`,
    skipped > 0 && `${skipped} already in the queue`,
    failed.length > 0 && `${failed.length} failed`,
  ]
    .filter(Boolean)
    .join(" · ")

  if (failed.length === 0) {
    toast.success(title)
    return
  }
  const listed = failed.slice(0, 3).map((o) => `${o.name}: ${o.reason}`)
  if (failed.length > 3) listed.push(`and ${failed.length - 3} more`)
  toast.error(title, { description: listed.join("; ") })
}

/**
 * Uploads picked invoice files to the queue.
 *
 * `onDone` runs once the batch has settled, when at least one file is now in the
 * queue (uploaded or already there), and only if the caller is still mounted: a batch
 * keeps going after the user leaves the page, and must not pull them back.
 */
export const useInvoiceUpload = ({ onDone }: { onDone?: () => void } = {}) => {
  const { me } = useLoggedInUserInfo()
  const organizationId = me.organization.id
  const queryClient = useQueryClient()
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const mounted = useRef(false)

  // Set on mount as well as cleared on unmount: StrictMode mounts twice
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const upload = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return

      const pending = [...files]
      const outcomes: Outcome[] = []
      setProgress({ done: 0, total: files.length })

      const worker = async () => {
        for (let file = pending.shift(); file; file = pending.shift()) {
          outcomes.push(await uploadFile(organizationId, file))
          setProgress((current) => current && { ...current, done: current.done + 1 })
        }
      }
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, files.length) }, worker),
      )

      await queryClient.invalidateQueries({
        queryKey: queryKeys.invoices.uploads(organizationId),
      })
      setProgress(null)
      showSummary(outcomes)
      if (mounted.current && outcomes.some((o) => o.result !== "failed")) onDone?.()
    },
    [organizationId, queryClient, onDone],
  )

  return { upload, progress }
}
